import React from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * CInput - A reusable enhanced input component
 */
const CInput = ({
  label,
  placeholder = "",
  value = "",
  onChange,
  sx = {},
  size = "small",
  fullWidth = false,
  variant = "outlined",
  tooltip,
  InputProps = {},
  multiline = false,
  rows = 1,
  disabled = false,
  uppercase = false,
  required = false,
  sessionKey = "",
  sessionField = "",
  useSessionStorage = false
}) => {
  // Handle clear button click
  const handleClear = () => {
    // Create synthetic event for the parent component's onChange
    const event = { target: { value: '' } };
    onChange(event);
  };

  // Handle input change
  const handleInputChange = (event) => {
    let modifiedEvent;
    
    // Handle uppercase conversion if enabled
    if (uppercase) {
      modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: event.target.value.toUpperCase()
        }
      };
    } else {
      modifiedEvent = event;
    }
    
    // Trigger parent onChange handler
    onChange(modifiedEvent);
  };

  // Only show clear button if there's a value and field is not disabled
  const showClearButton = value && !disabled;

  // Basic styles without responsive changes
  const basicStyles = {
    ...(uppercase && {
      '& .MuiInputBase-input': {
        textTransform: 'uppercase',
      },
      '& .MuiInputBase-input::placeholder': {
        textTransform: 'uppercase',
      },
    }),
  };

  // Combine input props with clear button
  const inputProps = {
    ...(InputProps.startAdornment && {
      startAdornment: InputProps.startAdornment
    }),
    endAdornment: showClearButton ? (
      <InputAdornment position="end">
        <IconButton
          aria-label="clear input"
          onClick={handleClear}
          edge="end"
          size="small"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : InputProps.endAdornment,
    ...InputProps
  };

  // Combine basic styles with user-provided styles
  const combinedStyles = {
    ...basicStyles,
    ...sx,
  };

  const inputElement = (
    <TextField
      label={label}
      placeholder={uppercase ? placeholder.toUpperCase() : placeholder}
      variant={variant}
      size={size}
      value={uppercase && typeof value === 'string' ? value.toUpperCase() : value}
      onChange={handleInputChange}
      fullWidth={fullWidth}
      InputProps={inputProps}
      multiline={multiline}
      rows={rows}
      disabled={disabled}
      required={required}
      InputLabelProps={{
        sx: {
          ...(required && {
            '&.Mui-required': {
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              }
            }
          })
        }
      }}
      sx={combinedStyles}
    />
  );

  return tooltip ? (
    <Tooltip 
      title={tooltip} 
      placement="top-start"
    >
      {inputElement}
    </Tooltip>
  ) : inputElement;
};

CInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  tooltip: PropTypes.string,
  InputProps: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  uppercase: PropTypes.bool,
  required: PropTypes.bool,
  sessionKey: PropTypes.string,
  sessionField: PropTypes.string,
  useSessionStorage: PropTypes.bool
};

export default CInput;